"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { useEffect } from "react";
import { cva } from "class-variance-authority";
import { cn } from "../utils.js";
import { useDropdownContext } from "./context.js";
import { DropdownBaseItem } from "./DropdownBaseItem.js";
import { DropdownBaseList } from "./DropdownBaseList.js";
const ICON_VARIANTS = {
    default: 'tw-bg-neutral-light-300 dark:tw-bg-neutral-dark-600 tw-text-neutral-light-700 dark:tw-text-neutral-dark-300 group-aria-selected:tw-bg-neutral-light-200 dark:group-aria-selected:tw-bg-neutral-dark-500',
    'default-selected': 'tw-text-primary-light-500 dark:tw-text-secondary-dark-500 dark:tw-bg-neutral-dark-700 dark:group-aria-selected:tw-bg-neutral-dark-700',
    primary: 'tw-text-primary-light-500 tw-bg-primary-light-100',
    'primary-selected': 'tw-text-primary-light-500 tw-bg-primary-light-100',
    success: 'tw-text-success-500 tw-bg-success-100 tw-bg-success-100',
    'success-selected': 'tw-text-success-500 tw-bg-success-100',
    alert: 'tw-text-alert-500 tw-bg-alert-100 tw-bg-alert-100',
    'alert-selected': 'tw-text-alert-500 tw-bg-alert-100',
    info: 'tw-text-info-500 tw-bg-info-100',
    'info-selected': 'tw-text-info-500 tw-bg-info-100',
    disabled: 'tw-text-neutral-light-300 dark:tw-text-neutral-dark-300'
};
const iconVariants = cva('tw-min-h-4 tw-min-w-4 tw-rounded-full tw-flex tw-items-center tw-justify-center', {
    variants: {
        variant: ICON_VARIANTS
    },
    defaultVariants: {
        variant: 'default'
    }
});
const getParsedVariant = ({ iconColorVariant = 'default', isSelected })=>isSelected ? `${iconColorVariant}-selected` : iconColorVariant;
const DropdownIconsList = ({ items, className, selected, contentClassName, iconClassName, ...itemProps })=>{
    const { values, setItems } = useDropdownContext();
    useEffect(()=>{
        setItems(items.map((item)=>({
                ...item,
                label: item.title,
                type: 'icon'
            })));
    }, [
        items
    ]);
    return /*#__PURE__*/ jsx(DropdownBaseList, {
        selected: selected,
        className: className,
        children: items.map((item)=>{
            const isSelected = values.includes(item.value);
            const { iconColorVariant } = item;
            return /*#__PURE__*/ jsx(DropdownBaseItem, {
                value: item.value,
                searchKey: item.title,
                isSelected: isSelected,
                className: cn('tw-min-h-6.4', isSelected && 'dark:tw-bg-neutral-dark-600', className, item.disabled && 'tw-cursor-not-allowed tw-pointer-events-none tw-bg-neutral-light-400'),
                ...itemProps,
                children: /*#__PURE__*/ jsxs("div", {
                    className: "tw-flex tw-items-center tw-gap-0.8",
                    children: [
                        /*#__PURE__*/ jsx("div", {
                            className: cn(iconVariants({
                                variant: getParsedVariant({
                                    iconColorVariant: item.disabled ? 'default' : iconColorVariant,
                                    isSelected: item.disabled ? false : isSelected
                                })
                            }), iconClassName),
                            children: item.icon({
                                size: item.iconSize || 24,
                                className: 'tw-text-inherit tw-flex-shrink-0'
                            })
                        }),
                        /*#__PURE__*/ jsxs("div", {
                            className: "tw-flex tw-flex-col tw-gap-0.4",
                            children: [
                                /*#__PURE__*/ jsx("div", {
                                    className: cn('tw-font-subtitle5 tw-line-clamp-1', contentClassName, isSelected && !iconColorVariant && 'tw-text-neutral-light-800 dark:tw-text-neutral-light-100 group-aria-selected:tw-text-primary-light-500 dark:group-aria-selected:tw-text-primary-light-100', isSelected && iconColorVariant && 'tw-text-neutral-light-800 dark:tw-text-neutral-light-100', item.disabled && 'tw-text-neutral-light-700 dark:tw-text-neutral-light-200'),
                                    children: item.title
                                }),
                                item.description && /*#__PURE__*/ jsx("div", {
                                    className: cn('tw-font-body5 tw-line-clamp-1 tw-text-neutral-light-700 dark:tw-text-neutral-dark-300', contentClassName),
                                    children: item.description
                                })
                            ]
                        })
                    ]
                })
            }, item.value);
        })
    });
};
export { DropdownIconsList };
